import React, { forwardRef, SelectHTMLAttributes } from 'react'
import { ChevronDown } from 'lucide-react'
import { clsx } from 'clsx'

interface SelectOption {
  value: string | number
  label: string
  disabled?: boolean
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string
  error?: string
  hint?: string
  options: SelectOption[]
  placeholder?: string
  isLoading?: boolean
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({
    label,
    error,
    hint,
    options,
    placeholder = 'Selecione uma opção',
    isLoading,
    className,
    disabled,
    ...props
  }, ref) => {
    const selectId = props.id || `select-${crypto.randomUUID()}`

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {label}
            {props.required && <span className="text-danger-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            disabled={disabled || isLoading}
            className={clsx(
              'block w-full rounded-md border shadow-sm',
              'focus:outline-none focus:ring-2 focus:ring-offset-0',
              'text-gray-900 appearance-none bg-white',
              'transition-colors duration-200',
              'text-base sm:text-sm',
              'pr-10 pl-3 sm:pl-4',
              'py-2 sm:py-2.5',
              'min-h-[44px]',
              error
                ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500'
                : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
              (disabled || isLoading) && 'bg-gray-50 text-gray-500 cursor-not-allowed',
              className
            )}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>

          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            {isLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
          </div>
        </div>

        {(error || hint) && (
          <div className="mt-1">
            {error && (
              <p className="text-sm text-danger-600">{error}</p>
            )}
            {hint && !error && (
              <p className="text-sm text-gray-500">{hint}</p>
            )}
          </div>
        )}
      </div>
    )
  }
)

Select.displayName = 'Select'

export default Select
